// src/stores/orgInstallStore.js
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import logger from "../utils/logger";

/**
 * Organization and Installation Store
 * 
 * Replaces OrgInstallContext for managing selected organization/installation state
 * and providing this data to all components that need it.
 * 
 * Features:
 * - Organization and installation selection
 * - Auto-navigation state for smart selector UX
 * - Loading state management
 * - Settings management (per-organization)
 */

const initialState = {
  // Core selection state
  organizations: [],
  selectedOrganization: null,
  selectedInstallation: null,
  
  // Loading state
  loading: true,
  loadingProgress: 0,
  
  // View/navigation state
  view: "loading", // 'loading', 'selectOrg', 'selectInstallation', 'app'
  cameFromApp: false,
  savedOrganization: null,
  savedInstallation: null,
  deletedCurrentInstallation: false,
  
  // Modal state
  showInstallationManagement: false,
  showOrganizationManagement: false,
  
  // Organization settings
  orgSettings: {
    enableDevStreamsForAllRoles: false,
  },
};

export const useOrgInstallStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // Actions
      setOrganizations: (organizations) => set({ organizations }),
      
      setSelectedOrganization: (org) => {
        set({ 
          selectedOrganization: org,
          // Reset installation when org changes (unless this is a restoration)
          selectedInstallation: get().cameFromApp ? get().selectedInstallation : null,
        });
        
        // Save to localStorage for persistence
        if (org) {
          localStorage.setItem("selectedOrganizationId", org.orgId);
          localStorage.setItem("selectedOrganizationName", org.orgName);
        } else {
          localStorage.removeItem("selectedOrganizationId");
          localStorage.removeItem("selectedOrganizationName");
        }
      },
      
      setSelectedInstallation: (installation) => {
        set({ selectedInstallation: installation });
        
        // Save to localStorage for persistence
        if (installation) {
          localStorage.setItem("selectedInstallationId", installation.installationId);
          localStorage.setItem("selectedInstallationName", installation.installationName);
        } else {
          localStorage.removeItem("selectedInstallationId");
          localStorage.removeItem("selectedInstallationName");
        }
      },
      
      setLoading: (loading) => set({ loading }),
      setLoadingProgress: (progress) => set({ loadingProgress: progress }),
      
      setView: (view) => {
        logger.log(`[OrgInstallStore] View changed to: ${view}`);
        set({ view });
      },
      
      setCameFromApp: (cameFromApp) => set({ cameFromApp }),
      setSavedOrganization: (org) => set({ savedOrganization: org }),
      setSavedInstallation: (installation) => set({ savedInstallation: installation }),
      setDeletedCurrentInstallation: (deleted) => set({ deletedCurrentInstallation: deleted }),
      
      setShowInstallationManagement: (show) => set({ showInstallationManagement: show }),
      setShowOrganizationManagement: (show) => set({ showOrganizationManagement: show }),
      
      setOrgSettings: (settings) => {
        set({ orgSettings: settings });
        
        // Cache to localStorage
        const org = get().selectedOrganization;
        if (org?.orgId) {
          const cacheKey = `orgSettings_${org.orgId}`;
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ settings, timestamp: Date.now() })
          );
        }
      },
      
      // Compound actions
      resetToOrg: () => {
        const { savedOrganization, savedInstallation } = get();
        set({
          selectedOrganization: savedOrganization,
          selectedInstallation: savedInstallation,
          view: "app",
          cameFromApp: false,
          savedOrganization: null,
          savedInstallation: null,
        });
      },
      
      handleOrgBackButton: () => {
        const state = get();
        if (state.cameFromApp) {
          // Return to app with saved org/installation
          state.resetToOrg();
        } else {
          // Normal back to org selector
          set({
            selectedOrganization: null,
            selectedInstallation: null,
            view: "selectOrg",
          });
        }
      },
      
      handleInstallationBackButton: () => {
        set({
          selectedInstallation: null,
          view: "selectInstallation",
        });
      },
      
      // For switching org/installation from within the app
      navigateToOrgSelector: () => {
        const state = get();
        set({
          cameFromApp: true,
          savedOrganization: state.selectedOrganization,
          savedInstallation: state.selectedInstallation,
          selectedOrganization: null,
          selectedInstallation: null,
          view: "selectOrg",
        });
      },
      
      navigateToInstallationSelector: () => {
        const state = get();
        set({
          cameFromApp: true,
          savedInstallation: state.selectedInstallation,
          selectedInstallation: null,
          view: "selectInstallation",
        });
      },
      
      // Reset all state (e.g., on logout)
      reset: () => {
        set(initialState);
        localStorage.removeItem("selectedOrganizationId");
        localStorage.removeItem("selectedOrganizationName");
        localStorage.removeItem("selectedInstallationId");
        localStorage.removeItem("selectedInstallationName");
      },
      
      // Get context object (for compatibility with old useOrgInstall pattern)
      getContext: () => {
        const state = get();
        return {
          organizationId: state.selectedOrganization?.orgId,
          organizationName: state.selectedOrganization?.orgName,
          installationId: state.selectedInstallation?.installationId,
          installationName: state.selectedInstallation?.installationName,
          organizations: state.organizations,
        };
      },
    }),
    {
      name: "org-install-store",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);

// Selector hooks for optimized re-renders
export const useOrganizationId = () => 
  useOrgInstallStore((state) => state.selectedOrganization?.orgId);

export const useInstallationId = () => 
  useOrgInstallStore((state) => state.selectedInstallation?.installationId);

export const useOrgInstallContext = () => 
  useOrgInstallStore((state) => ({
    organizationId: state.selectedOrganization?.orgId,
    organizationName: state.selectedOrganization?.name,
    installationId: state.selectedInstallation?.installationId,
    installationName: state.selectedInstallation?.name,
    organizations: state.organizations,
  }), shallow);
